import { AlertTriangle, Braces, CheckCircle2, Eye, Plus, Search } from 'lucide-react-native';
import { useRef, useState } from 'react';
import {
  type NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInput,
  type TextInputKeyPressEventData,
  type TextInputSelectionChangeEventData,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, { FadeInDown, FadeOutUp, ReduceMotion } from 'react-native-reanimated';
import { FormInput } from '../../../components/primitives/FormInput';
import { Body, Caption, Heading4, Label, Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { PromptVariableDefinition } from '../types';

type ScriptField = 'opening' | 'cta' | 'closing';
type Selection = { start: number; end: number };
type VariableAutocomplete = { field: ScriptField; start: number; cursor: number; query: string };

interface ScriptVariableBuilderProps {
  opening: string;
  cta: string;
  closing: string;
  variables: PromptVariableDefinition[];
  isLoading?: boolean;
  isError?: boolean;
  onOpeningChange: (value: string) => void;
  onCtaChange: (value: string) => void;
  onClosingChange: (value: string) => void;
}

const TOKEN_PATTERN = /{{\s*([^{}|]+?)(?:\|[^{}]+)?\s*}}/g;

function variableKeys(script: string): string[] {
  return Array.from(script.matchAll(TOKEN_PATTERN), (match) => match[1]?.trim() ?? '').filter(
    Boolean,
  );
}

function resolveExamples(script: string, variables: ReadonlyMap<string, PromptVariableDefinition>) {
  return script.replace(TOKEN_PATTERN, (_token, rawKey: string) => {
    const key = rawKey.trim();
    const variable = variables.get(key);
    return variable?.example ?? `[${key}]`;
  });
}

function detectVariableAutocomplete(
  field: ScriptField,
  value: string,
  cursor: number,
): VariableAutocomplete | null {
  const beforeCursor = value.slice(0, cursor);
  const match = beforeCursor.match(/\$([a-z_][a-z0-9_.]*|)$/i);
  if (!match) return null;

  const start = cursor - match[0].length;
  const precedingCharacter = start > 0 ? value[start - 1] : '';
  if (precedingCharacter && /[a-z0-9_]/i.test(precedingCharacter)) return null;

  return { field, start, cursor, query: match[1] ?? '' };
}

function HighlightedPrompt({
  field,
  script,
  variables,
  label,
}: {
  field: ScriptField;
  script: string;
  variables: ReadonlyMap<string, PromptVariableDefinition>;
  label: string;
}) {
  const parts = [];
  let cursor = 0;

  for (const match of script.matchAll(TOKEN_PATTERN)) {
    const start = match.index ?? 0;
    const key = match[1]?.trim() ?? '';
    if (start > cursor) parts.push(script.slice(cursor, start));
    parts.push(
      <Text
        key={`${start}-${key}`}
        size="sm"
        weight="semibold"
        color={variables.has(key) ? colors.primary[300] : colors.status.error.light}
        accessibilityLabel={`prompt-variable-tag-${key}`}
        style={variables.has(key) ? styles.highlightedVariable : styles.highlightedVariableError}
      >
        {`$${key}`}
      </Text>,
    );
    cursor = start + match[0].length;
  }
  if (cursor < script.length) parts.push(script.slice(cursor));

  return (
    <View style={styles.syntaxMap} accessible accessibilityLabel={`highlighted-prompt-${field}`}>
      <Caption style={styles.syntaxLabel}>{label}</Caption>
      <Text size="sm" color={colors.text.description} style={styles.syntaxText}>
        {parts}
      </Text>
    </View>
  );
}

export function ScriptVariableBuilder({
  opening,
  cta,
  closing,
  variables,
  isLoading = false,
  isError = false,
  onOpeningChange,
  onCtaChange,
  onClosingChange,
}: ScriptVariableBuilderProps) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isWide = width >= 980;
  const [activeField, setActiveField] = useState<ScriptField>('opening');
  const [search, setSearch] = useState('');
  const [autocomplete, setAutocomplete] = useState<VariableAutocomplete | null>(null);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(0);
  const selections = useRef<Partial<Record<ScriptField, Selection>>>({});
  const inputRefs = useRef<Partial<Record<ScriptField, TextInput | null>>>({});

  const values: Record<ScriptField, string> = { opening, cta, closing };
  const changeHandlers: Record<ScriptField, (value: string) => void> = {
    opening: onOpeningChange,
    cta: onCtaChange,
    closing: onClosingChange,
  };
  const variablesByKey = new Map(variables.map((variable) => [variable.key, variable]));
  const usedKeys = [...new Set(variableKeys(`${opening}\n${cta}\n${closing}`))];
  const unknownKeys = usedKeys.filter((key) => !variablesByKey.has(key));
  const normalizedSearch = search.trim().toLowerCase();
  const filteredVariables = variables.filter((variable) =>
    `${variable.label} ${variable.key} ${variable.category}`
      .toLowerCase()
      .includes(normalizedSearch),
  );
  const autocompleteVariables = autocomplete
    ? variables
        .filter((variable) => {
          const query = autocomplete.query.toLowerCase();
          return (
            variable.key.toLowerCase().includes(query) ||
            variable.label.toLowerCase().includes(query)
          );
        })
        .slice(0, 6)
    : [];

  function insertVariable(variable: PromptVariableDefinition) {
    const value = values[activeField];
    const selection = selections.current[activeField] ?? { start: value.length, end: value.length };
    const token = `{{${variable.key}}}`;
    const nextValue = `${value.slice(0, selection.start)}${token}${value.slice(selection.end)}`;
    const nextCursor = selection.start + token.length;

    changeHandlers[activeField](nextValue);
    selections.current[activeField] = { start: nextCursor, end: nextCursor };
    inputRefs.current[activeField]?.focus();
  }

  function selectionHandler(field: ScriptField) {
    return (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
      const selection = event.nativeEvent.selection;
      selections.current[field] = selection;
      if (selection.start === selection.end) {
        setAutocomplete(detectVariableAutocomplete(field, values[field], selection.start));
        setHighlightedSuggestionIndex(0);
      }
    };
  }

  function changeText(field: ScriptField, nextValue: string) {
    const currentValue = values[field];
    const selection = selections.current[field] ?? {
      start: currentValue.length,
      end: currentValue.length,
    };
    const insertedLength =
      nextValue.length - (currentValue.length - (selection.end - selection.start));
    const cursor = Math.max(selection.start, selection.start + insertedLength);

    selections.current[field] = { start: cursor, end: cursor };
    changeHandlers[field](nextValue);
    setActiveField(field);
    setAutocomplete(detectVariableAutocomplete(field, nextValue, cursor));
    setHighlightedSuggestionIndex(0);
  }

  function completeVariable(variable: PromptVariableDefinition) {
    if (!autocomplete) return;
    const value = values[autocomplete.field];
    const token = `{{${variable.key}}}`;
    const nextValue = `${value.slice(0, autocomplete.start)}${token}${value.slice(autocomplete.cursor)}`;
    const cursor = autocomplete.start + token.length;

    changeHandlers[autocomplete.field](nextValue);
    selections.current[autocomplete.field] = { start: cursor, end: cursor };
    inputRefs.current[autocomplete.field]?.focus();
    setAutocomplete(null);
    setHighlightedSuggestionIndex(0);
  }

  function handleAutocompleteKeyPress(
    field: ScriptField,
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) {
    if (autocomplete?.field !== field) return;

    const { key } = event.nativeEvent;
    if (key === 'Escape') {
      event.preventDefault();
      setAutocomplete(null);
      setHighlightedSuggestionIndex(0);
      return;
    }
    if (autocompleteVariables.length === 0) return;

    if (key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedSuggestionIndex((index) => (index + 1) % autocompleteVariables.length);
      return;
    }
    if (key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedSuggestionIndex(
        (index) => (index - 1 + autocompleteVariables.length) % autocompleteVariables.length,
      );
      return;
    }
    if (key === 'Enter' || key === 'Tab') {
      event.preventDefault();
      const variable = autocompleteVariables[highlightedSuggestionIndex];
      if (variable) completeVariable(variable);
    }
  }

  const fields: Array<{
    key: ScriptField;
    label: string;
    a11y: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
  }> = [
    {
      key: 'opening',
      label: t('voiceAgents.wizard.script.openingLabel'),
      a11y: t('voiceAgents.wizard.script.openingA11y'),
      placeholder: t('voiceAgents.wizard.script.openingPlaceholder'),
      value: opening,
      onChange: onOpeningChange,
    },
    {
      key: 'cta',
      label: t('voiceAgents.wizard.script.ctaLabel'),
      a11y: t('voiceAgents.wizard.script.ctaA11y'),
      placeholder: t('voiceAgents.wizard.script.ctaPlaceholder'),
      value: cta,
      onChange: onCtaChange,
    },
    {
      key: 'closing',
      label: t('voiceAgents.wizard.script.closingLabel'),
      a11y: t('voiceAgents.wizard.script.closingA11y'),
      placeholder: t('voiceAgents.wizard.script.closingPlaceholder'),
      value: closing,
      onChange: onClosingChange,
    },
  ];

  return (
    <View style={[styles.workbench, isWide && styles.workbenchWide]}>
      <View style={styles.editorColumn}>
        <View style={styles.introRow}>
          <View style={styles.introIcon}>
            <Braces size={18} color={colors.primary.main} strokeWidth={1.8} />
          </View>
          <View style={styles.introCopy}>
            <Heading4>{t('voiceAgents.wizard.script.builderTitle')}</Heading4>
            <Caption style={styles.muted}>{t('voiceAgents.wizard.script.builderHint')}</Caption>
          </View>
          <View style={styles.safeBadge}>
            <CheckCircle2 size={13} color={colors.primary.main} />
            <Caption style={styles.safeText}>{t('voiceAgents.wizard.script.liveSafe')}</Caption>
          </View>
        </View>

        {fields.map((field) => {
          const fieldKeys = [...new Set(variableKeys(field.value))];
          const isActive = activeField === field.key;
          return (
            <View
              key={field.key}
              style={[styles.sectionCard, isActive && styles.sectionCardActive]}
            >
              <View style={styles.sectionHeader}>
                <Label>{field.label}</Label>
                <Caption style={styles.characterCount}>{field.value.length}</Caption>
              </View>
              <FormInput
                ref={(node) => {
                  inputRefs.current[field.key] = node;
                }}
                value={field.value}
                onChangeText={(value) => changeText(field.key, value)}
                onFocus={() => setActiveField(field.key)}
                onSelectionChange={selectionHandler(field.key)}
                onKeyPress={(event) => handleAutocompleteKeyPress(field.key, event)}
                placeholder={field.placeholder}
                accessibilityLabel={field.a11y}
                multiline
                textAlignVertical="top"
                style={[styles.scriptInput, field.key === 'opening' && styles.openingInput]}
              />
              {autocomplete?.field === field.key ? (
                <Animated.View
                  style={styles.autocomplete}
                  accessibilityRole="menu"
                  accessibilityLabel={`variable-autocomplete-${field.key}`}
                  entering={FadeInDown.duration(140).reduceMotion(ReduceMotion.System)}
                  exiting={FadeOutUp.duration(100).reduceMotion(ReduceMotion.System)}
                >
                  <View style={styles.autocompleteHeader}>
                    <Caption style={styles.autocompleteTitle}>
                      {t('voiceAgents.wizard.script.autocompleteTitle')}
                    </Caption>
                    <Caption style={styles.autocompleteQuery}>{`$${autocomplete.query}`}</Caption>
                  </View>
                  <Caption style={styles.autocompleteKeyboardHint}>
                    {t('voiceAgents.wizard.script.autocompleteKeyboardHint')}
                  </Caption>
                  {autocompleteVariables.map((variable, index) => {
                    const isHighlighted = index === highlightedSuggestionIndex;
                    return (
                      <Pressable
                        key={variable.key}
                        onPress={() => completeVariable(variable)}
                        accessibilityRole="menuitem"
                        accessibilityLabel={`autocomplete-variable-${variable.key}`}
                        accessibilityState={{ selected: isHighlighted }}
                        aria-selected={isHighlighted}
                        style={({ pressed }) => [
                          styles.autocompleteOption,
                          isHighlighted && styles.autocompleteOptionHighlighted,
                          pressed && styles.autocompleteOptionPressed,
                        ]}
                      >
                        <View style={styles.autocompleteOptionCopy}>
                          <Text size="sm" weight="semibold">
                            {variable.label}
                          </Text>
                          <Caption style={styles.variableKey}>{`$${variable.key}`}</Caption>
                        </View>
                        <Plus size={14} color={colors.primary.main} />
                      </Pressable>
                    );
                  })}
                  {autocompleteVariables.length === 0 ? (
                    <Caption style={styles.autocompleteEmpty}>
                      {t('voiceAgents.wizard.script.autocompleteEmpty')}
                    </Caption>
                  ) : null}
                </Animated.View>
              ) : null}
              {fieldKeys.length > 0 ? (
                <HighlightedPrompt
                  field={field.key}
                  script={field.value}
                  variables={variablesByKey}
                  label={t('voiceAgents.wizard.script.variableMap')}
                />
              ) : null}
              <View style={styles.usedRow}>
                <Caption style={styles.usedLabel}>
                  {t('voiceAgents.wizard.script.usedVariables')}
                </Caption>
                {fieldKeys.length === 0 ? (
                  <Caption style={styles.muted}>
                    {t('voiceAgents.wizard.script.noVariables')}
                  </Caption>
                ) : (
                  fieldKeys.map((key) => (
                    <View
                      key={key}
                      style={[styles.usedChip, !variablesByKey.has(key) && styles.errorChip]}
                      accessible
                      accessibilityLabel={`used-variable-${key}`}
                    >
                      <Text
                        size="xs"
                        color={
                          variablesByKey.has(key) ? colors.primary[300] : colors.status.error.main
                        }
                      >
                        {`$${key}`}
                      </Text>
                    </View>
                  ))
                )}
              </View>
            </View>
          );
        })}

        {unknownKeys.map((key) => (
          <View
            key={key}
            style={styles.validationError}
            accessible
            accessibilityLabel={`script-variable-error-${key}`}
          >
            <AlertTriangle size={15} color={colors.status.error.main} />
            <Caption style={styles.errorText}>
              {t('voiceAgents.wizard.script.unknownVariable', { variable: key })}
            </Caption>
          </View>
        ))}

        <View style={styles.previewCard}>
          <View style={styles.previewHeader}>
            <Eye size={15} color={colors.text.secondary} />
            <Label>{t('voiceAgents.wizard.script.previewTitle')}</Label>
            <Caption style={styles.previewSection}>
              {t(`voiceAgents.wizard.script.${activeField}Short`)}
            </Caption>
          </View>
          <Body selectable accessibilityLabel="script-resolved-preview" style={styles.previewText}>
            {resolveExamples(values[activeField], variablesByKey) ||
              t('voiceAgents.wizard.script.previewEmpty')}
          </Body>
        </View>
      </View>

      <View style={[styles.variablePanel, isWide && styles.variablePanelWide]}>
        <View>
          <Heading4>{t('voiceAgents.wizard.script.variableLibrary')}</Heading4>
          <Caption style={styles.muted}>
            {t('voiceAgents.wizard.script.variableLibraryHint')}
          </Caption>
        </View>
        <View style={styles.searchWrap}>
          <Search size={15} color={colors.text.tertiary} />
          <FormInput
            value={search}
            onChangeText={setSearch}
            placeholder={t('voiceAgents.wizard.script.searchVariables')}
            accessibilityLabel={t('voiceAgents.wizard.script.searchVariablesA11y')}
            style={styles.searchInput}
          />
        </View>
        {isLoading ? (
          <Caption style={styles.muted}>{t('voiceAgents.wizard.script.loadingVariables')}</Caption>
        ) : null}
        {isError ? (
          <Caption style={styles.errorText}>
            {t('voiceAgents.wizard.script.loadVariablesError')}
          </Caption>
        ) : null}
        <ScrollView
          style={styles.variableScroll}
          contentContainerStyle={styles.variableList}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
        >
          {filteredVariables.map((variable) => (
            <Pressable
              key={variable.key}
              onPress={() => insertVariable(variable)}
              accessibilityRole="button"
              accessibilityLabel={`insert-variable-${variable.key}`}
              style={({ pressed }) => [styles.variableCard, pressed && styles.variableCardPressed]}
            >
              <View style={styles.variableCopy}>
                <Text size="sm" weight="semibold">
                  {variable.label}
                </Text>
                <Caption style={styles.variableKey}>{`$${variable.key}`}</Caption>
                <Caption style={styles.variableExample}>
                  {t('voiceAgents.wizard.script.examplePrefix', {
                    example: variable.example,
                  })}
                </Caption>
              </View>
              <View style={styles.addVariableIcon}>
                <Plus size={14} color={colors.primary.main} strokeWidth={2} />
              </View>
            </Pressable>
          ))}
          {!isLoading && filteredVariables.length === 0 ? (
            <Caption style={styles.emptyVariables}>
              {t('voiceAgents.wizard.script.noVariableResults')}
            </Caption>
          ) : null}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  workbench: { gap: tokens.spacing.lg },
  workbenchWide: { flexDirection: 'row', alignItems: 'flex-start' },
  editorColumn: { flex: 1, gap: tokens.spacing.md },
  introRow: { flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.smPlus },
  introIcon: {
    width: 36,
    height: 36,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary.faint,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  introCopy: { flex: 1, gap: tokens.spacing.xxs },
  safeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.smPlus,
    paddingVertical: tokens.spacing.sm,
    backgroundColor: colors.primary.faint,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  safeText: { color: colors.primary[300] },
  muted: { color: colors.text.tertiary },
  sectionCard: {
    gap: tokens.spacing.sm,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.surface.row,
    padding: tokens.spacing.md,
  },
  sectionCardActive: { borderColor: colors.primary.borderActive },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  characterCount: { color: colors.text.disabled, fontVariant: ['tabular-nums'] },
  scriptInput: {
    minHeight: 82,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.smPlus,
    fontSize: tokens.fontSize.md,
    lineHeight: 24,
  },
  openingInput: { minHeight: 118 },
  autocomplete: {
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.background.elevated,
    padding: tokens.spacing.sm,
  },
  autocompleteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.xs,
    paddingBottom: tokens.spacing.xs,
  },
  autocompleteTitle: {
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: tokens.letterSpacing.wider,
  },
  autocompleteQuery: { color: colors.primary[300], fontFamily: 'monospace' },
  autocompleteKeyboardHint: {
    color: colors.text.disabled,
    paddingHorizontal: tokens.spacing.xs,
    paddingBottom: tokens.spacing.xs,
  },
  autocompleteOption: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.smPlus,
    paddingHorizontal: tokens.spacing.smPlus,
    paddingVertical: tokens.spacing.sm,
    backgroundColor: colors.surface.row,
  },
  autocompleteOptionHighlighted: {
    backgroundColor: colors.primary.faint,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  autocompleteOptionPressed: { backgroundColor: colors.primary.faint },
  autocompleteOptionCopy: { flex: 1, gap: tokens.spacing.xxs },
  autocompleteEmpty: {
    color: colors.text.tertiary,
    textAlign: 'center',
    paddingVertical: tokens.spacing.md,
  },
  usedRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacing.xs },
  usedLabel: { color: colors.text.tertiary, paddingRight: tokens.spacing.xs },
  usedChip: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: colors.primary.faint,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  errorChip: { backgroundColor: colors.status.error.bg, borderColor: colors.status.error.border },
  syntaxMap: {
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    paddingHorizontal: tokens.spacing.smPlus,
    paddingVertical: tokens.spacing.sm,
  },
  syntaxLabel: {
    color: colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: tokens.letterSpacing.wider,
  },
  syntaxText: { lineHeight: 25 },
  highlightedVariable: {
    backgroundColor: colors.primary.muted,
    borderRadius: tokens.radius.sm,
  },
  highlightedVariableError: {
    backgroundColor: colors.status.error.bg,
    borderRadius: tokens.radius.sm,
  },
  validationError: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.status.error.border,
    backgroundColor: colors.status.error.bg,
    padding: tokens.spacing.smPlus,
  },
  errorText: { color: colors.status.error.main },
  previewCard: {
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
    padding: tokens.spacing.mdPlus,
  },
  previewHeader: { flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.sm },
  previewSection: {
    marginLeft: 'auto',
    color: colors.primary[300],
    textTransform: 'uppercase',
    letterSpacing: tokens.letterSpacing.wider,
  },
  previewText: { color: colors.text.description, lineHeight: 23 },
  variablePanel: {
    gap: tokens.spacing.md,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
    backgroundColor: colors.background.elevated,
    padding: tokens.spacing.mdPlus,
  },
  variablePanelWide: { width: 340, maxHeight: 690 },
  searchWrap: {
    minHeight: 42,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.smPlus,
  },
  searchInput: { flex: 1, paddingVertical: tokens.spacing.sm, fontSize: tokens.fontSize.smPlus },
  variableScroll: { flexGrow: 0 },
  variableList: { gap: tokens.spacing.sm },
  variableCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.row,
    padding: tokens.spacing.smPlus,
  },
  variableCardPressed: {
    borderColor: colors.primary.border,
    backgroundColor: colors.primary.faint,
  },
  variableCopy: { flex: 1, gap: tokens.spacing.xxs },
  variableKey: { color: colors.primary[300], fontFamily: 'monospace' },
  variableExample: { color: colors.text.tertiary },
  addVariableIcon: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary.faint,
  },
  emptyVariables: { color: colors.text.tertiary, textAlign: 'center', padding: tokens.spacing.lg },
});
